import { BASE_URL } from '../Api';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { PluginDownloadButtons, FreeHighlight } from '../partials';

const TakeawayPlugin = () => {
  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Dine-in & Takeaway - Aronium</title>
          <meta name="description" content="Let customers choose Takeaway or Dine-in, print order type on kitchen tickets, and emphasize large order numbers on Takeaway receipts." />
        </Helmet>
      </HelmetProvider>

      {/* Hero */}
      <section className="py-8 max-w-screen-xl px-4 mx-auto">
        <div className="max-w-screen-xl mx-auto text-center">
          <h1 className="text-4xl mb-4">Dine-in & Takeaway</h1>
          <p>
            Choose between <span className="font-semibold">Dine‑in</span> and <span className="font-semibold">Takeaway</span> and print large order number in kitchen tickets and receipts for faster pickup.
          </p>

          {/* Call to Action Buttons */}
          <PluginDownloadButtons
            liteUrl={`${BASE_URL}/plugins/takeaway/latest/Aronium.Pos.Takeaway.Lite.exe`}
            proUrl={`${BASE_URL}/plugins/takeaway/latest/Aronium.Pos.Takeaway.Pro.exe`} />
        </div>
      </section>

      {/* Visual */}
      <div className="flex flex-col text-center mt-10 grid place-content-center justify-items-center px-4">
        <img
          src="/images/service-type.png"
          className='rounded-t-xl xl:max-w-6xl'
          alt="Dine-in or Takeaway selection"
        />
      </div>

      {/* Overview */}
      <div className="bg-gray-100 py-20">
        <div className="max-w-7xl mx-auto text-center px-8 lg:px-12">
          <h2 className="text-5xl text-gray-800 leading-tight mb-8">
            Why use the <span className="font-semibold">Takeaway</span> plugin?
          </h2>
          <p className="text-lg text-gray-600 mx-auto">
            The <span className="font-semibold">Takeaway</span> plugin adds a simple, mandatory choice between <em>Dine-in</em> and <em>Takeaway</em> when creating an order. The chosen type is
            printed on kitchen tickets so cooks instantly know plating and packaging requirements. For <em>Takeaway</em> orders, receipts emphasize a <span className="font-semibold">large order number</span>,
            helping staff call out pickups quickly and reduce counter crowding.
          </p>
        </div>
        <FreeHighlight className="mt-12" />
      </div>

      {/* Key Features */}
      <div className="py-24">
        <div className="max-w-6xl mx-auto px-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
          <div className="bg-white p-8 shadow-lg rounded-lg border border-gray-200">
            <h3 className="text-2xl font-semibold text-gray-800">Dine-in vs Takeaway selector</h3>
            <p className="mt-4 text-gray-600">
              Prompt staff to choose the order type at the start or allow changing it later from the order screen.
            </p>
          </div>

          <div className="bg-white p-8 shadow-lg rounded-lg border border-gray-200">
            <h3 className="text-2xl font-semibold text-gray-800">Kitchen ticket labeling</h3>
            <p className="mt-4 text-gray-600">
              Print the selected type on kitchen tickets so the back-of-house instantly knows whether to plate or pack.
            </p>
          </div>

          <div className="bg-white p-8 shadow-lg rounded-lg border border-gray-200">
            <h3 className="text-2xl font-semibold text-gray-800">Large order number on Takeaway receipts</h3>
            <p className="mt-4 text-gray-600">
              Emphasize a big, bold order number for Takeaway to speed up pickup calls and reduce mix-ups at the counter.
            </p>
          </div>

          <div className="bg-white p-8 shadow-lg rounded-lg border border-gray-200">
            <h3 className="text-2xl font-semibold text-gray-800">Flexible configuration</h3>
            <p className="mt-4 text-gray-600">
              Choose whether the selector appears automatically on new orders, allow manual overrides, and align print behavior with your workflow.
            </p>
          </div>
        </div>
      </div>

      {/* Tips / Best Practices */}
      <div className="bg-white py-16">
        <div className="max-w-6xl mx-auto px-6">
          <h3 className="text-3xl font-semibold text-gray-800 text-center">Tips for smoother daily operations</h3>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <p className="text-gray-700">
                <span className="font-semibold inline-flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6 mr-1 -ml-1">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 0 0 1.5-.189m-1.5.189a6.01 6.01 0 0 1-1.5-.189m3.75 7.478a12.06 12.06 0 0 1-4.5 0m3.75 2.383a14.406 14.406 0 0 1-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 1 0-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
                  </svg>Streamline packing vs plating</span>
                <p className='mt-1'>Kitchen tickets that clearly show the order type prevent mistakes and save prep time.</p>
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <p className="text-gray-700">
                <span className="font-semibold inline-flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6 mr-1 -ml-1">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 0 0 1.5-.189m-1.5.189a6.01 6.01 0 0 1-1.5-.189m3.75 7.478a12.06 12.06 0 0 1-4.5 0m3.75 2.383a14.406 14.406 0 0 1-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 1 0-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
                  </svg>Speed up pickups</span>
                <p className='mt-1'>The large Takeaway order number lets staff call customers quickly and match bags without re-reading items.</p>
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <p className="text-gray-700">
                <span className="font-semibold inline-flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6 mr-1 -ml-1">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 0 0 1.5-.189m-1.5.189a6.01 6.01 0 0 1-1.5-.189m3.75 7.478a12.06 12.06 0 0 1-4.5 0m3.75 2.383a14.406 14.406 0 0 1-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 1 0-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
                  </svg>Reduce dine-in disruptions</span>
                <p className='mt-1'>Marking Dine-in keeps table service smooth and avoids putting dine-in meals into takeaway packaging.</p>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="relative bg-gradient-to-r from-emerald-900 to-emerald-500 py-24 text-center text-white">
        <div className="relative mx-auto px-4">
          <h2 className="text-4xl xl:text-6xl mb-6">Ready to speed up your service?</h2>
          <p className="text-xl mb-10">
            Start using the <span className="font-semibold">Takeaway</span> plugin to keep your kitchen and front counter in sync.
          </p>
          <a
            href="https://help.aronium.com/hc/en-us/sections/30836811569554-Dine-in-Takeaway?utm_source=website&utm_medium=link&utm_campaign=dine-in-takeaway-page"
            className="button-rounded-white px-12 py-4"
            target="_blank"
            rel="noreferrer"
          >
            Learn more
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="ml-2 w-6 h-6 transition-all"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 8.25 21 12m0 0-3.75 3.75M21 12H3" />
            </svg>
          </a>
        </div>
      </div>
    </>
  );
};

export default TakeawayPlugin;
